
import { supabase } from "@/integrations/supabase/client";

interface ApiCallData {
  source_menu_item: string;
  platform: 'openai' | 'gemini' | 'claude' | 'perplexity' | 'dataforseo' | 'firecrawl' | 'fal_ai' | 'google' | 'hailuoai' | 'byteplus' | 'lumalabs';
  endpoint?: string;
  method?: string;
  request_size?: number;
  response_size?: number;
  status_code?: number;
  duration_ms?: number;
  cost_estimate?: number;
  metadata?: any;
  brand_id?: string;
  generation_type?: string; // For visual generation tracking (image, video, etc.)
  model_used?: string; // For AI model tracking (veo3, flux-schnell, etc.)
}

export class ApiCallsAuditService {
  private static startTime: number;

  static startTracking() {
    this.startTime = Date.now();
  }

  static async logApiCall(data: ApiCallData) {
    try {
      const duration = this.startTime ? Date.now() - this.startTime : undefined;
      
      console.log('=== Logging API call ===');
      console.log('API call data:', {
        platform: data.platform,
        source: data.source_menu_item,
        endpoint: data.endpoint,
        generation_type: data.generation_type,
        model_used: data.model_used
      });
      
      const { error } = await supabase
        .from('api_calls_audit')
        .insert({
          user_id: (await supabase.auth.getUser()).data.user?.id,
          timestamp: new Date().toISOString(),
          source_menu_item: data.source_menu_item,
          platform: data.platform.toLowerCase(), // Ensure consistent lowercase
          endpoint: data.endpoint,
          method: data.method || 'POST',
          request_size: data.request_size,
          response_size: data.response_size,
          status_code: data.status_code,
          duration_ms: duration,
          cost_estimate: data.cost_estimate,
          metadata: data.metadata || {},
          brand_id: data.brand_id,
          generation_type: data.generation_type,
          model_used: data.model_used
        });

      if (error) {
        console.error('Failed to log API call:', error);
      } else {
        console.log('API call logged successfully');
      }
    } catch (error) {
      console.error('Error logging API call:', error);
    }
  }

  static estimateCost(
    platform: string,
    tokens?: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number } | number,
    model?: string,
    isImage?: boolean,
    imageParams?: { quality?: string; size?: string }
  ): number {
    const MODEL_PRICING: Record<string, { input: number; output: number }> = {
      // GPT-5 series
      'gpt-5-2025-08-07': { input: 0.01, output: 0.03 },
      'gpt-5-mini-2025-08-07': { input: 0.001, output: 0.003 },
      'gpt-5-nano-2025-08-07': { input: 0.0001, output: 0.0003 },
      
      // GPT-4.1 series
      'gpt-4.1-2025-04-14': { input: 0.005, output: 0.015 },
      'gpt-4.1-mini-2025-04-14': { input: 0.001, output: 0.003 },
      
      // O-series
      'o3-2025-04-16': { input: 0.02, output: 0.06 },
      'o4-mini-2025-04-16': { input: 0.002, output: 0.006 },
      
      // Legacy GPT-4o
      'gpt-4o': { input: 0.005, output: 0.015 },
      'gpt-4o-mini': { input: 0.00015, output: 0.0006 },
    };

    switch (platform.toLowerCase()) {
      case 'openai':
        // Handle new token-based pricing
        if (typeof tokens === 'object' && tokens !== null) {
          const promptTokens = tokens.prompt_tokens || 0;
          const completionTokens = tokens.completion_tokens || 0;
          
          // Find model-specific pricing
          const pricing = model ? MODEL_PRICING[model] : null;
          
          if (pricing) {
            return (promptTokens * pricing.input + completionTokens * pricing.output) / 1000;
          }
          
          // Fallback to legacy model detection
          if (model?.includes('gpt-4o-mini')) {
            return (promptTokens * 0.00015 + completionTokens * 0.0006) / 1000;
          }
          if (model?.includes('gpt-4o')) {
            return (promptTokens * 0.005 + completionTokens * 0.015) / 1000;
          }
          if (model?.includes('gpt-4.1')) {
            return (promptTokens * 0.005 + completionTokens * 0.015) / 1000;
          }
          
          // Default fallback
          return (promptTokens * 0.00015 + completionTokens * 0.0006) / 1000;
        }
        
        // Legacy: simple token count (backward compatibility)
        const tokenCount = typeof tokens === 'number' ? tokens : 1000;
        if (model?.includes('gpt-4o-mini')) return tokenCount * 0.00015 / 1000;
        if (model?.includes('gpt-4o')) return tokenCount * 0.005 / 1000;
        if (model?.includes('gpt-4.1')) return tokenCount * 0.01 / 1000;
        if (model?.includes('gpt-4')) return tokenCount * 0.00003;
        return tokenCount * 0.0000015;
        
      case 'gemini':
        const geminiTokens = typeof tokens === 'number' ? tokens : (tokens?.total_tokens || 1000);
        return geminiTokens * 0.000001;
        
      case 'claude':
        const claudeTokens = typeof tokens === 'number' ? tokens : (tokens?.total_tokens || 1000);
        if (model?.includes('opus')) return claudeTokens * 0.015 / 1000;
        if (model?.includes('sonnet')) return claudeTokens * 0.003 / 1000;
        if (model?.includes('haiku')) return claudeTokens * 0.00025 / 1000;
        return claudeTokens * 0.003 / 1000;
        
      case 'perplexity':
        const perplexityTokens = typeof tokens === 'number' ? tokens : (tokens?.total_tokens || 1000);
        return perplexityTokens * 0.001 / 1000;
        
      case 'dataforseo':
        return 0.01;
        
      case 'firecrawl':
        return 0.005;
        
      case 'fal_ai':
        return 0.008;
        
      default:
        return 0;
    }
  }

  // Helper method to log OpenAI API calls specifically
  static async logOpenAICall(data: {
    source_menu_item: string;
    brand_id: string;
    model: string;
    endpoint: string;
    status_code: number;
    duration_ms: number;
    tokens?: {
      prompt_tokens: number;
      completion_tokens: number;
      total_tokens: number;
      estimated_tokens?: boolean;
    };
    metadata?: any;
  }) {
    const costEstimate = data.tokens 
      ? this.estimateCost('openai', data.tokens, data.model)
      : 0;

    return this.logApiCall({
      ...data,
      platform: 'openai',
      cost_estimate: costEstimate,
      metadata: {
        ...data.metadata,
        ...data.tokens
      }
    });
  }

  // Helper method to log DataForSEO API calls specifically
  static async logDataForSEOCall(data: {
    source_menu_item: string;
    endpoint?: string;
    method?: string;
    request_size?: number;
    response_size?: number;
    status_code?: number;
    cost_estimate?: number;
    metadata?: any;
    brand_id?: string;
  }) {
    return this.logApiCall({
      ...data,
      platform: 'dataforseo'
    });
  }

  // Helper method to log Firecrawl API calls specifically
  static async logFirecrawlCall(data: {
    source_menu_item: string;
    endpoint?: string;
    method?: string;
    request_size?: number;
    response_size?: number;
    status_code?: number;
    cost_estimate?: number;
    metadata?: any;
    brand_id?: string;
  }) {
    return this.logApiCall({
      ...data,
      platform: 'firecrawl'
    });
  }

  // Helper method to log Claude API calls specifically
  static async logClaudeCall(data: {
    source_menu_item: string;
    endpoint?: string;
    method?: string;
    request_size?: number;
    response_size?: number;
    status_code?: number;
    cost_estimate?: number;
    metadata?: any;
    brand_id?: string;
  }) {
    return this.logApiCall({
      ...data,
      platform: 'claude'
    });
  }

  // Helper method to log Perplexity API calls specifically
  static async logPerplexityCall(data: {
    source_menu_item: string;
    endpoint?: string;
    method?: string;
    request_size?: number;
    response_size?: number;
    status_code?: number;
    cost_estimate?: number;
    metadata?: any;
    brand_id?: string;
  }) {
    return this.logApiCall({
      ...data,
      platform: 'perplexity'
    });
  }
}
